import { buildClusterFaqJsonLdAsync } from "@/lib/clusterFaq";
import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import SiteCoreLinks from "@/components/SiteCoreLinks";
import AnswerBoxTop from "@/components/AnswerBoxTop";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
// /visa — hub for all visa × nationality combinations
import { createFileRoute, Link } from "@tanstack/react-router";
import { DirectAnswer, KeyFactsTable } from "@/components/seo/Blocks";
import { VoiceSearchFAQ } from "@/components/seo/VoiceSearchFAQ";
import { speakableSchema, serviceWithReviewsSchema, howToSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { VISA_NATIONALITIES, VISA_REGIONS } from "@/data/visa-nationalities";
import { VISA_TYPES } from "@/data/visa-types";
import { consolidateGraph } from "@/lib/schema-graph";
import { Plane, ArrowRight, ShieldCheck } from "lucide-react";
import { TrustLayer } from "@/components/TrustLayer";
import HubLeadCTA from "@/components/HubLeadCTA";
import { TranslateVisaCrossLink } from "@/components/TranslateVisaCrossLink";
import { RelatedComparisons } from "@/components/RelatedComparisons";
import { AIAnswerBox } from "@/components/AIAnswerBox";
import { claimReviewSchema, softwareApplicationSchema, entitySameAsSchema, COMMON_CLAIM_REBUTTALS_TH } from "@/lib/trust-schema";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/visa/")({
  loader: async () => ({
    clusterFaq: await buildClusterFaqJsonLdAsync("visa", "th", "https://nyclanguageinstitute.com/visa"),
    voiceFaqs: (await import("@/data/voice-faqs")).VISA_VOICE_FAQS,
  }),
  head: ({ loaderData }: any) => {
    const url = `${SITE}/visa`;
    const title = `วีซ่าไทย ${VISA_NATIONALITIES.length} สัญชาติ × ${VISA_TYPES.length} ประเภท · บริการครบวงจร | NYC`;
    const desc = `บริการวีซ่าไทยครบทุกประเภท (Non-B, LTR, DTV, Elite, Retirement, Marriage, ED) สำหรับ ${VISA_NATIONALITIES.length} สัญชาติ — ยื่นเอกสารแทน + พาไปสถานทูต — คุยกับผู้เชี่ยวชาญตัวจริงได้เลยวันนี้`;
    return {
      meta: [
        { title: ctaTitle(title, "/visa", "th") }, { name: "description", content: ctaDesc(desc, "/visa", "th")},
        { property: "og:title", content: ctaTitle(title, "/visa", "th")}, { property: "og:description", content: ctaDesc(desc, "/visa", "th")},
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks("/visa")],
      scripts: [speakableSchema(),
        howToSchema({
          name: "วิธีเตรียมเอกสารยื่นวีซ่าไทย / วีซ่าต่างประเทศ",
          description: "ขั้นตอนเตรียมเอกสารแปล + รับรอง สำหรับยื่นวีซ่าไทย (Non-B, LTR, DTV, Retirement, Marriage) และวีซ่าต่างประเทศ (Schengen, US, UK, AU)",
          totalTime: "PT10D", estimatedCost: { currency: "THB", value: "8000" },
          steps: [
            { name: "เช็ค Checklist เอกสารตามประเภทวีซ่า", text: "เปิด Checklist วีซ่าที่ต้องการ (Non-B, LTR, DTV, Marriage, Retirement) เพื่อดูเอกสารที่ต้องเตรียม", url: `${SITE}/checklist` },
            { name: "รวบรวมเอกสารต้นฉบับ", text: "ทะเบียนบ้าน · สูติบัตร · ทะเบียนสมรส · วุฒิการศึกษา · Bank Statement · Police Check ตามที่ Checklist ระบุ" },
            { name: "แปลเอกสารโดยนักแปลรับรอง", text: "แปลเป็นภาษาอังกฤษ (หรือภาษาปลายทาง) โดยทีม NYC ที่ขึ้นทะเบียนกระทรวงยุติธรรม", url: `${SITE}/translate` },
            { name: "รับรอง MFA + Apostille / Legalization", text: "ส่งกรมการกงสุล + Apostille (Hague) หรือ Consular Legalization สำหรับประเทศนอก Hague", url: `${SITE}/apostille` },
            { name: "ยื่นที่สถานทูต / ตม.", text: "นัดสถานทูตปลายทาง หรือ ตม.ไทย พร้อมเอกสารครบ — เรามีบริการพาไปยื่น (Walk-in Service)" },
          ],
        }),
        consolidateGraph([
          serviceWithReviewsSchema({ path: "/visa", serviceType: "Thailand Visa Application Service", name: title, description: desc, priceFromThb: 8000 }),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CollectionPage", "@id": `${url}#page`,
              name: title, description: desc,
              hasPart: VISA_NATIONALITIES.map((n) => ({
                "@type": "WebPage", name: `วีซ่าไทยสำหรับคน${n.nameTh}`,
                url: `${SITE}/visa/${n.slug}`,
              })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                { "@type": "ListItem", position: 2, name: "วีซ่า", item: url },
              ],
            }),
          },
        ]),
        entitySameAsSchema(),
        softwareApplicationSchema({ url: `${SITE}/api/public/mcp`, name: "NYC Language Institute — MCP Server", description: "AI agent endpoint exposing Thai visa consulting tooling for all nationalities." }),
        claimReviewSchema(COMMON_CLAIM_REBUTTALS_TH(url)),
      
        ...(loaderData?.clusterFaq ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.clusterFaq) }] : []),
      ],
    };
  },
  component: VisaHub,
});

function VisaHub() {
  const { voiceFaqs } = Route.useLoaderData();
  const url = `${SITE}/visa`;
  return (
    <main className="min-h-screen bg-white">
      <div className="mx-auto max-w-6xl px-4 pt-6">
        <AIAnswerBox
          question={`บริการขอวีซ่าไทยของ NYC ครอบคลุมอะไรบ้าง?`}
          answer={`NYC ให้บริการยื่นวีซ่าไทย ${VISA_TYPES.length}+ ประเภท (Non-B, LTR, DTV, Elite, Retirement, Marriage, ED) สำหรับ ${VISA_NATIONALITIES.length} สัญชาติ — พาเข้าสถานทูต/กงสุล/ตม.แจ้งวัฒนะ พร้อมแปลและรับรองเอกสารครบในจุดเดียว ตอบกลับใน 15 นาที`}
          stats={[
            { label: "ประเภทวีซ่า", value: `${VISA_TYPES.length}+` },
            { label: "สัญชาติ", value: `${VISA_NATIONALITIES.length}` },
            { label: "ตอบกลับ", value: "15 นาที" },
            { label: "DBD", value: "0435567000061" },
          ]}
          sourceUrl={url}
          lang="th"
        />
      </div>
      <section className="bg-navy-deep text-white">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Plane className="h-3.5 w-3.5" /> Visa Service · {VISA_NATIONALITIES.length} Nationalities
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            วีซ่าไทย {VISA_NATIONALITIES.length} สัญชาติ × {VISA_TYPES.length} ประเภท
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            Non-B, LTR, DTV, Elite, Retirement, Marriage, ED — ครบทุกประเภท พร้อมพาไปสถานทูต / ตม.
          </p>
          <div className="mt-6 grid gap-4 lg:grid-cols-[1.1fr_1fr]">
            <DirectAnswer entity="NYC Thai Visa Service">
              NYC Language Institute ให้บริการยื่นวีซ่าไทย {VISA_TYPES.length}+ ประเภทสำหรับ {VISA_NATIONALITIES.length} สัญชาติ ครอบคลุม Non-Immigrant B (ธุรกิจ), LTR, DTV (Digital Nomad), Elite, Retirement (O-A/O-X), Marriage, ED — พาเข้าสถานทูต / กงสุล / ตม. แจ้งวัฒนะ พร้อมแปลและรับรองเอกสารครบในจุดเดียว ตอบกลับใน 15 นาที
            </DirectAnswer>
            <KeyFactsTable rows={[
              { k: "ประเภทวีซ่า", v: `${VISA_TYPES.length}+ ประเภท` },
              { k: "สัญชาติ", v: `${VISA_NATIONALITIES.length} สัญชาติ` },
              { k: "บริการ", v: "ยื่น + พาไปสัมภาษณ์ + แปลเอกสาร" },
              { k: "หน่วยงาน", v: "สถานทูต · ตม. · กงสุล" },
              { k: "ตอบกลับ", v: "ภายใน 15 นาที" },
              { k: "DBD", v: "0435567000061" },
            ]} />
          </div>
        </div>
      </section>

      <QuotableStatsStrip cluster="visa" locale="th" variant="dark" />

      <AnswerBoxTop cluster="visa" locale="th" />

      <section className="mx-auto max-w-6xl px-4 sm:px-6 mt-8 sm:mt-10">
        <VoiceSearchFAQ topic="Thai Visa Service" faqs={voiceFaqs} entity="NYC Thai Visa Service" />
      </section>

      <section className="px-4">
        <div className="mx-auto max-w-6xl rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm leading-7 text-slate-700">
          <h2 className="font-display text-xl font-extrabold text-navy-deep">เอกสารประกอบวีซ่าที่ต้องแปลและรับรอง</h2>
          <p className="mt-2">
            คู่มือรวม 6 กลุ่มเอกสารประกอบคำร้องขอวีซ่า พร้อมลำดับขั้นตอนแปล–รับรองนิติกรณ์–สถานทูต และเช็กลิสต์เตรียมเอกสารของแต่ละกลุ่ม
          </p>
          <Link to="/visa/documents" className="mt-3 inline-block font-bold text-amber-700 hover:underline">
            อ่านคู่มือเอกสารประกอบวีซ่า →
          </Link>
        </div>
      </section>


      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
        {VISA_REGIONS.map((region) => {
          const list = VISA_NATIONALITIES.filter((n) => n.region === region.id);
          if (!list.length) return null;
          return (
            <section key={region.id} className="mb-12">
              <h2 className="font-display text-2xl font-extrabold text-navy-deep">{region.label}</h2>
              <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {list.map((n) => (
                  <Link key={n.slug} to="/visa/$nationality" params={{ nationality: n.slug }}
                    className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md">
                    <div className="text-3xl leading-none">{n.flag}</div>
                    <div className="flex-1">
                      <div className="font-display text-base font-extrabold text-navy-deep group-hover:text-amber-600">
                        วีซ่าสำหรับคน{n.nameTh}
                      </div>
                      <div className="mt-0.5 text-[11px] text-slate-500">{n.countryEn}</div>
                      <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-600">
                        <ShieldCheck className="h-3 w-3 text-emerald-600" />
                        {n.visaExempt > 0 ? `Visa Exempt ${n.visaExempt} วัน` : "ต้องขอวีซ่าล่วงหน้า"} · {n.topVisaTypes.length} ประเภท
                      </div>
                    </div>
                    <ArrowRight className="mt-1 h-4 w-4 text-slate-400 group-hover:text-amber-600" />
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
      </div>

      <TranslateVisaCrossLink mode="from-visa" locale="th" />
      <RelatedComparisons category="visa" locale="th" />
      <HubLeadCTA locale="th" service="Thailand Visa" />
      <TrustLayer variant="compact" />
          <SiteCoreLinks />
    
      <PlanningToolsLinks locale="th" />
      </main>
  );
}
